import React, { useState, useEffect } from 'react';
import { ExternalLink, Calendar, Clock, BookOpen } from 'lucide-react';

const MEDIUM_USERNAME = 'aditya.design';
const MEDIUM_RSS_URL = `https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@${MEDIUM_USERNAME}`;

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMediumPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch(MEDIUM_RSS_URL);
        const data = await response.json();
        
        if (data.status === 'ok' && data.items) {
          const latestPosts = data.items.slice(0, 3).map(item => ({
            title: item.title,
            link: item.link,
            pubDate: new Date(item.pubDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            }),
            thumbnail: item.thumbnail || extractFirstImage(item.content) || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&q=80',
            description: stripHtml(item.description).substring(0, 120) + '...',
            categories: item.categories || [],
            readTime: calculateReadTime(item.content)
          }));
          setPosts(latestPosts);
        } else {
          setError('Unable to fetch blog posts');
        }
      } catch (err) {
        console.error('Error fetching Medium posts:', err);
        setError('Unable to fetch blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchMediumPosts();
  }, []);

  const extractFirstImage = (content) => {
    const imgRegex = /<img[^>]+src="([^">]+)"/;
    const match = content.match(imgRegex);
    return match ? match[1] : null;
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const calculateReadTime = (content) => {
    const text = stripHtml(content);
    const wordsPerMinute = 200;
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime;
  };

  if (loading) {
    return (
      <div className="section-alt">
        <section className="section">
          <div className="section-header">
            <span className="section-label">Insights</span>
            <h2 className="section-title">Latest from Medium</h2>
          </div>
          <div className="blog-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="blog-card skeleton-card">
                <div className="skeleton-image"></div>
                <div className="blog-card-content">
                  <div className="skeleton-text"></div>
                  <div className="skeleton-text short"></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="section-alt">
        <section className="section">
          <div className="section-header">
            <span className="section-label">Insights</span>
            <h2 className="section-title">Latest from Medium</h2>
            <p className="subtitle">
              Thoughts on UX design, research, and human-centered experiences
            </p>
          </div>
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <BookOpen size={40} color="var(--text-tertiary)" style={{ marginBottom: '12px' }} />
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '0.9375rem' }}>
              Check out my latest articles on Medium
            </p>
            <a 
              href={`https://medium.com/@${MEDIUM_USERNAME}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="medium-profile-link"
            >
              Visit Medium Profile
              <ExternalLink size={14} style={{ marginLeft: '6px' }} />
            </a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="section-alt">
      <section className="section" data-testid="blog-section">
        <div className="section-header">
          <span className="section-label">Insights</span>
          <h2 className="section-title">Latest from Medium</h2>
          <p className="subtitle">
            Thoughts on UX design, research, and human-centered experiences
          </p>
        </div>

        <div className="blog-grid" data-testid="blog-grid">
          {posts.map((post, index) => (
            <a 
              key={index}
              href={post.link}
              target="_blank"
              rel="noopener noreferrer"
              className="blog-card"
              data-testid={`blog-card-${index}`}
            >
              <div className="blog-card-image">
                <img src={post.thumbnail} alt={post.title} />
                <div className="blog-card-overlay">
                  <span className="read-article">
                    Read Article <ExternalLink size={12} />
                  </span>
                </div>
              </div>
              <div className="blog-card-content">
                <div className="blog-meta">
                  <span className="blog-date">
                    <Calendar size={12} />
                    {post.pubDate}
                  </span>
                  <span className="blog-read-time">
                    <Clock size={12} />
                    {post.readTime} min
                  </span>
                </div>
                <h3 className="blog-title">{post.title}</h3>
                <p className="blog-excerpt">{post.description}</p>
                {post.categories.length > 0 && (
                  <div className="blog-tags">
                    {post.categories.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="blog-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <a 
            href={`https://medium.com/@${MEDIUM_USERNAME}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            data-testid="view-all-articles"
          >
            View All Articles on Medium
            <ExternalLink size={14} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default BlogSection;
